import React, { useContext, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { AuthContext } from '../context/AuthContext';
import { useAgenda } from '../hooks/useAgenda';
import { sanitizeEmail } from '../utils/email';

const ResultsPreview = ({ results, searchParams }) => {
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [toast, setToast] = useState(null); // { type: 'success' | 'error' | 'info', message: string }
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; // You can make this configurable
    const [saveLoading, setSaveLoading] = useState(false);
    const { user, signInWithGoogle } = useContext(AuthContext);
    const { items, saveManyPlacesToAgenda, canAddMore, maxFree } = useAgenda();

    const savedIds = useMemo(() => {
        return new Set((items || []).map((item) => item.placeId));
    }, [items]);

    const totalPages = Math.ceil(results.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedResults = results.slice(startIndex, endIndex);

    const selectableResults = useMemo(() => {
        return results.filter((place) => {
            const rowId = place.placeId || place.place_id || place.id || `${place.name || 'sin-nombre'}_${place.address || 'sin-direccion'}`;
            return !savedIds.has(rowId);
        });
    }, [results, savedIds]);

    const allSelected = selectableResults.length > 0 && selectableResults.every((place) => {
        const rowId = place.placeId || place.place_id || place.id || `${place.name || 'sin-nombre'}_${place.address || 'sin-direccion'}`;
        return selectedIds.has(rowId);
    });

    const showToast = (type, message, duration = 800) => {
        setToast({ type, message });
        window.clearTimeout(showToast._timer);
        showToast._timer = window.setTimeout(() => setToast(null), duration);
    };

    const toggleSelect = (rowId, isSaved, checked) => {
        if (!rowId || isSaved) return;
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(rowId);
            else next.delete(rowId);
            return next;
        });
    };

    const clearSelection = () => setSelectedIds(new Set());

    const handleSelectAll = (checked) => {
        if (checked) {
            const newSelected = new Set(selectedIds);
            selectableResults.forEach((place) => {
                const rowId = place.placeId || place.place_id || place.id || `${place.name || 'sin-nombre'}_${place.address || 'sin-direccion'}`;
                newSelected.add(rowId);
            });
            setSelectedIds(newSelected);
        } else {
            const newSelected = new Set(selectedIds);
            selectableResults.forEach((place) => {
                const rowId = place.placeId || place.place_id || place.id || `${place.name || 'sin-nombre'}_${place.address || 'sin-direccion'}`;
                newSelected.delete(rowId);
            });
            setSelectedIds(newSelected);
        }
    };

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleBulkSave = async () => {
        setSaveLoading(true);
        try {
            if (!user) {
                signInWithGoogle();
                setSaveLoading(false);
                return;
            }
            const selectable = results
                .map((place) => {
                    const rowId = place.placeId || place.place_id || place.id;
                    const isSaved = rowId && savedIds.has(rowId);
                    if (!rowId || isSaved || !selectedIds.has(rowId)) return null;
                    return {
                        placeId: rowId,
                        name: place.name,
                        address: place.address,
                        phone: place.phone,
                        website: place.website,
                        email: sanitizeEmail(place.email),
                        city: searchParams?.address || '',
                        category: searchParams?.category || '',
                    };
                })
                .filter(Boolean);

            if (selectable.length === 0) {
                showToast('info', 'No hay nuevos clientes para crear');
                return;
            }

            const { saved, skipped } = await saveManyPlacesToAgenda(selectable);
            if (saved > 0) {
                showToast('success', `Creados ${saved} clientes manualmente`);
            }
            if (skipped > 0) {
                showToast('info', `Se omitieron ${skipped} duplicados (mismo número de teléfono o ya guardados)`);
            }

            clearSelection();
        } catch (err) {
            if (err.code === 'LIMIT_REACHED' || err.code === 'FREE_LIMIT_REACHED') {
                showToast('error', `Límite FREE de ${maxFree} clientes alcanzado`);
            } else if (err.code === 'AUTH_REQUIRED') {
                showToast('info', 'Inicia sesión para guardar contactos');
            } else {
                showToast('error', 'No se pudo guardar la selección');
            }
        } finally {
            setSaveLoading(false);
        }
    };

    const hasSelection = selectedIds.size > 0;

    return (
        <div className="results-preview">
            {saveLoading && (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>Guardando {selectedIds.size} contacto(s)...</p>
                </div>
            )}
            {toast && (
                <div className={`toast toast-${toast.type}`}>
                    <span className="toast-icon">✓</span>
                    <span>{toast.message}</span>
                </div>
            )}
            <h2>Resultados ({results.length})</h2>
            {!canAddMore && (
                <p className="freemium-warning">
                    Has alcanzado el máximo de {maxFree} contactos creados en el plan FREE.
                </p>
            )}
            <div className="table-container">
                <table className="results-table">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Dirección</th>
                            <th>Maps</th>
                            <th>Teléfono</th>
                            <th>Sitio Web</th>
                            <th>Email</th>
                            <th>Rating</th>
                            <th>Reseñas</th>
                            <th>
                                <input
                                    type="checkbox"
                                    className="checkbox-select"
                                    checked={allSelected}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    title="Seleccionar todos"
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedResults.map((place, index) => {
                            const rowId =
                                place.placeId ||
                                place.place_id ||
                                place.id ||
                                `${place.name || 'sin-nombre'}_${place.address || 'sin-direccion'}`;
                            const isSaved = rowId && savedIds.has(rowId);
                            const isSelected = rowId && selectedIds.has(rowId);
                            return (
                                <tr key={index} className={isSaved ? 'row-saved' : ''}>
                                    <td>
                                        <div className="name-cell">
                                            {isSaved && (
                                                <span
                                                    className="agenda-check"
                                                    title="Tienes este contacto en tu agenda"
                                                    aria-label="En agenda"
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <rect x="2" y="2" width="16" height="16" rx="4" fill="#22c55e" />
                                                        <path d="M6 10.5L9 13.5L14 8.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </span>
                                            )}
                                            <span
                                                className="name-short"
                                                title={place.name}
                                            >
                                                {place.name && place.name.length > 34
                                                    ? place.name.slice(0, 34) + '...'
                                                    : place.name || 'N/A'}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="address-cell">
                                            <span
                                                className="address-short"
                                                title={place.address}
                                            >
                                                {place.address && place.address.length > 28
                                                    ? place.address.slice(0, 28) + '...'
                                                    : place.address || 'N/A'}
                                            </span>
                                        </div>
                                    </td>
                                    <td>{place.address ? <a href={`https://maps.google.com/?q=${encodeURIComponent(place.address)}`} target="_blank" rel="noopener noreferrer" title="Abrir en Google Maps">📍</a> : 'N/A'}</td>
                                    <td>{place.phone ? <a href={`tel:${place.phone}`}>{place.phone}</a> : 'N/A'}</td>
                                    <td>{place.website ? <a href={place.website} target="_blank" rel="noopener noreferrer">Visitar</a> : 'N/A'}</td>
                                    <td>
                                        <div className="email-cell">
                                            {(() => {
                                                const cleanEmail = sanitizeEmail(place.email);
                                                return (
                                                    <span
                                                        className="email-short"
                                                        title={cleanEmail || undefined}
                                                    >
                                                        {cleanEmail
                                                            ? (cleanEmail.length > 28 ? cleanEmail.slice(0, 28) + '...' : cleanEmail)
                                                            : 'N/A'}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                    </td>
                                    <td>{place.rating || 'N/A'}</td>
                                    <td>{place.reviews || 0}</td>
                                    <td>
                                        <input
                                            className="checkbox-select"
                                            type="checkbox"
                                            checked={!!isSelected}
                                            onChange={(e) => toggleSelect(rowId, isSaved, e.target.checked)}
                                            disabled={isSaved}
                                        />
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            {totalPages > 1 && (
                <div className="pagination">
                    <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                        Anterior
                    </button>
                    <span>Página {currentPage} de {totalPages}</span>
                    <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                        Siguiente
                    </button>
                </div>
            )}
            {hasSelection && canAddMore && ReactDOM.createPortal(
                <button
                    className="fab-save"
                    onClick={handleBulkSave}
                    disabled={saveLoading}
                >
                    {saveLoading ? 'Guardando...' : `Crear ${selectedIds.size} ${selectedIds.size === 1 ? 'Contacto' : 'Contactos'}`}
                </button>,
                document.body
            )}
        </div>
    );
};

export default ResultsPreview;
